import 'cropperjs/dist/cropper.css';
import { bind } from 'decko';
import { inject } from 'dependable-react';
import { css } from 'emotion';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import Cropper from 'react-cropper';

import iconRotate from '../assets/magicBuilder/iconRotate.svg';
import iconRotateOut from '../assets/magicBuilder/iconRotateOut.svg';
import iconTrash from '../assets/magicBuilder/iconTrash.svg';
import iconZoom from '../assets/magicBuilder/iconZoom.svg';
import iconZoomOut from '../assets/magicBuilder/iconZoomOut.svg';
import { BREAKPOINT_LARGE, BREAKPOINT_MEDIUM, BREAKPOINT_SMALL } from '../consts/breakpoints';
import { I18N_TOKEN } from '../services/InjectTokens';
import { Button } from './Button';
import { InputRange } from './InputRange';

const main = css`
  background: white;
  border-radius: 10px;
  border: 1px solid #e8e7e7;
  padding: 20px;
  position: fixed;
  top: 50px;
  bottom: 50px;
  left: 120px;
  right: 120px;
  z-index: 3;
  @media (max-width: ${BREAKPOINT_MEDIUM}) {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
  }
  overflow: auto;
`;

const backdrop = css`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 2;
  background-color: rgba(215, 203, 212, 0.9);
`;

const title = css`
  display: block;
  text-align: center;
  width: 100%;
`;

const hideInput = css`
  position: absolute;
  top: -9999px;
`;

const container = css`
  display: flex;
  justify-content: center;
  height: 290px;
  width: 290px;
  overflow: hidden;
  position: relative;
  margin: 0 auto;
  @media (min-width: ${BREAKPOINT_SMALL}) {
    height: 350px;
    width: 350px;
  }
  @media (min-width: ${BREAKPOINT_MEDIUM}) {
    height: 450px;
    width: 450px;
  }
  @media (min-width: ${BREAKPOINT_LARGE}) {
    height: 515px;
    width: 515px;
  }
`;

const actions = css`
  padding: 20px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  @media (min-width: 430px) {
    flex-direction: row;
  }
  > div {
    margin: 8px 0;
    @media (min-width: 430px) {
      margin: 0 20px;
    }
  }
`;

const cropperStyle = css`
  width: 100%;
  height: 100%;
  .cropper-line,
  .cropper-point {
    background-color: #fff;
  }
  .cropper-view-box {
    outline: #72659f;
    outline-color: #72659f;
  }
  .cropper-crop-box {
    transform: translate(-50%, -50%);
  }
`;

const controls = css`
  width: 100%;
  margin: 20px 0;
`;

const controlStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 200px;
  margin: 5px auto;
`;

const iconContainer = css`
  width: 54px;
  height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const removePhoto = css`
  position: absolute;
  top: 10px;
  right: 15px;
  z-index: 5;
  cursor: pointer;
`;

interface IProps {
  className?: string;
  onClose(croppedImage?: Blob | null, originalFileName?: string): void;
}

interface IState {
  cropper: Cropper | null;
  file: File | null;
  pickerOpen: boolean;
  zoom: number;
  rotate: number;
  maxRotate: number;
  maxZoom: number;
  minRotate: number;
  minZoom: number;
  [key: string]: any;
}

@observer
export class UploadModal extends React.Component<IProps> {
  private i18n = inject(I18N_TOKEN);

  private inputRef = React.createRef<HTMLInputElement>();

  @observable
  private componentState: IState = {
    cropper: null,
    file: null,
    maxRotate: 360,
    maxZoom: 2,
    minRotate: 0,
    minZoom: 0,
    pickerOpen: false,
    rotate: 0,
    zoom: 1,
  };

  @computed
  private get selectedImageURL() {
    if (this.componentState.file) {
      return URL.createObjectURL(this.componentState.file);
    }

    return '';
  }

  public render() {
    return (
      <React.Fragment>
        {!this.selectedImageURL && (
          <input
            className={hideInput}
            id="file"
            name="file"
            accept="image/*"
            type="file"
            onChange={this.onFileSelect}
            ref={this.inputRef}
          />
        )}
        {this.selectedImageURL && (
          <React.Fragment>
            <div className={backdrop} />
            <div className={main}>
              <div className={container}>
                <img
                  src={iconTrash}
                  alt="Remove"
                  role="button"
                  className={removePhoto}
                  onClick={this.removeUploadedPhoto}
                />
                <Cropper
                  //@ts-ignore
                  rotatable
                  dragMode="move"
                  className={cropperStyle}
                  zoomOnWheel={false}
                  zoomOnTouch={false}
                  cropBoxMovable={false}
                  cropBoxResizable={false}
                  ref={this.setCroperRef}
                  src={this.selectedImageURL}
                  aspectRatio={1 / 1}
                  viewMode={1}
                />
              </div>

              <h2 className={title}>{this.i18n.t('image_upload_title')}</h2>

              <div className={controls}>
                <div className={controlStyle}>
                  <div className={iconContainer} role="button" onClick={this.handleZoom('out', -0.1)}>
                    <img src={iconZoomOut} alt="Zoom out" />
                  </div>
                  <InputRange
                    max={this.componentState.maxZoom}
                    min={this.componentState.minZoom}
                    step={0.1}
                    value={this.componentState.zoom}
                    onChange={this.handleControlChange('zoom')}
                  />
                  <div className={iconContainer} role="button" onClick={this.handleZoom('in', 0.1)}>
                    <img src={iconZoom} alt="Zoom in" />
                  </div>
                </div>
                <div className={controlStyle}>
                  <div className={iconContainer} role="button" onClick={this.handleRotate('out', -15)}>
                    <img src={iconRotateOut} alt="Rotate -" />
                  </div>
                  <InputRange
                    max={this.componentState.maxRotate}
                    min={this.componentState.minRotate}
                    step={15}
                    value={this.componentState.rotate}
                    onChange={this.handleControlChange('rotate')}
                  />
                  <div className={iconContainer} role="button" onClick={this.handleRotate('in', 15)}>
                    <img src={iconRotate} alt="Rotate +" />
                  </div>
                </div>
              </div>

              <div className={actions}>
                <Button onClick={this.onSave} value={this.i18n.t('save_image')} />
                <Button secondary onClick={this.onCancel} value={this.i18n.t('cancel')} />
              </div>
            </div>
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }

  @bind
  @action
  private handleZoom(direction: string, amount: number) {
    return () => {
      const { cropper, maxZoom, minZoom, zoom } = this.componentState;
      if (direction === 'in' && zoom >= maxZoom) {
        return;
      }
      if (direction === 'out' && zoom <= minZoom) {
        return;
      }

      if (cropper) {
        this.componentState.zoom = this.componentState.zoom + amount;
        cropper.zoom(amount);
      }
    };
  }

  @bind
  @action
  private handleRotate(direction: string, amount: number) {
    return () => {
      const { cropper, maxRotate, minRotate, rotate } = this.componentState;
      if (direction === 'in' && rotate >= maxRotate) {
        return;
      }

      if (direction === 'out' && rotate <= minRotate) {
        return;
      }
      this.componentState.rotate = this.componentState.rotate + amount;
      if (cropper) {
        cropper.rotate(amount);
      }
    };
  }

  @bind
  @action
  private removeUploadedPhoto() {
    this.componentState.file = null;
  }

  @bind
  @action
  private handleControlChange(control: string) {
    const { cropper } = this.componentState;

    return (value: any) => {
      this.componentState[control] = value;

      if (cropper && control === 'zoom') {
        cropper.zoomTo(value);
      }

      if (cropper && control === 'rotate') {
        cropper.rotateTo(value);
      }
    };
  }

  @bind
  private onFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files) {
      this.componentState.file = e.target.files[0];
    }
  }

  @bind
  private setCroperRef(ref: any) {
    this.componentState.cropper = ref;
  }

  @bind
  private async onSave() {
    const { file, cropper } = this.componentState;
    if (file && cropper) {
      cropper.getCroppedCanvas().toBlob(
        (blob) => {
          this.props.onClose(blob, file.name);
          this.componentState.file = null;
        },
        'image/jpeg',
        0.9,
      );
    }
  }

  @bind
  private onCancel() {
    this.componentState.file = null;
  }
}
